import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { NotebookPen, Video, X } from "lucide-react";
import { formatDateTime } from "@/utils/formatDate";
import { DeviceSettingsDialog } from "../components/DeviceSettingsDialog";
import { GuestNotesPanel } from "../components/GuestNotesPanel";
import { MeetingControls } from "../components/MeetingControls";
import { VideoTileGrid } from "../components/VideoTileGrid";
import { guestGetMeeting } from "../api/meetingsApi";
import { useMediaDevices } from "../hooks/useMediaDevices";
import { useMeetingRoom } from "../hooks/useMeetingRoom";
import { createGuestTransport } from "../hooks/roomTransport";
import { playNoteChime } from "../sound";
import type { GuestMeetingPreview } from "../types";

/**
 * Public guest page (/meet/:token) — OUTSIDE the protected layout: no auth
 * store, no sidebar, no JWT-bearing requests. The invite token in the URL is
 * the only credential and unlocks exactly this one meeting.
 */
export function GuestMeetingPage() {
  const { token } = useParams<{ token: string }>();
  const devices = useMediaDevices();
  const qc = useQueryClient();

  const [preview, setPreview] = useState<GuestMeetingPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [name, setName] = useState(
    () => localStorage.getItem("elg5:guest-name") ?? "",
  );
  const [entered, setEntered] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Live override of the join-time notes flag when the organizer toggles it.
  const [notesUnlockedLive, setNotesUnlockedLive] = useState<boolean | null>(
    null,
  );

  const transport = useMemo(() => createGuestTransport(token ?? ""), [token]);
  const room = useMeetingRoom({
    occurrenceId: "guest", // real id arrives in the join contract; logger uses it below
    transport,
    devicePrefs: devices.prefs,
    // Live notes for the guest: the room channel carries note_changed events;
    // refresh the read-only notes query and chime when one lands.
    onNoteChanged: () => {
      void qc.invalidateQueries({ queryKey: ["guest-notes", token] });
      playNoteChime();
    },
    // Organizer toggled guest-notes visibility → reflect it live and open the
    // panel automatically when it becomes available.
    onNotesVisibility: (enabled) => {
      setNotesUnlockedLive(enabled);
      if (enabled) {
        setNotesOpen(true);
        void qc.invalidateQueries({ queryKey: ["guest-notes", token] });
      }
    },
  });

  // Notes are shown to the guest only when the organizer unlocked them: the live
  // toggle wins, else the flag from the join contract / preview.
  const notesVisible =
    notesUnlockedLive ??
    (room.contract?.occurrence as { notes_visible?: boolean } | undefined)
      ?.notes_visible ??
    preview?.notes_visible ??
    false;

  useEffect(() => {
    if (!token) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const load = () => {
      guestGetMeeting(token)
        .then((p) => {
          if (cancelled) return;
          setPreview(p);
          setName((cur) => cur || p.suggested_name || "");
          // Guests click emailed links early; without re-checking, the join
          // button stayed dead until a manual reload even after the meeting
          // opened (join window / organizer starting early).
          if (!p.can_join && p.status !== "ended" && p.status !== "cancelled") {
            timer = setTimeout(load, 20_000);
          }
        })
        .catch((err: Error & { status?: number }) => {
          if (cancelled) return;
          // Only the server SAYING the link is bad is final. A network blip
          // (sleeping laptop, wifi drop) must not replace the page with
          // "link invalid" — and must not stop the polling either.
          if (err?.status && err.status >= 400 && err.status < 500) {
            setPreviewError(
              "Ten link do spotkania jest nieprawidłowy, wygasł lub został unieważniony.",
            );
            return;
          }
          timer = setTimeout(load, 20_000);
        });
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // In-app webviews (Gmail/Outlook/Teams open links in one) ship without
  // getUserMedia/RTCPeerConnection — the guest would join deaf-mute with a
  // misleading mic-permission hint. Detect and say the actionable thing.
  const rtcUnsupported =
    typeof RTCPeerConnection === "undefined" ||
    !navigator.mediaDevices?.getUserMedia;

  const enter = async () => {
    localStorage.setItem("elg5:guest-name", name.trim() || "Gość");
    setEntered(true);
    await room.join();
    void devices.refresh();
  };

  // Device labels appear only once a capture is granted — refresh when local
  // media becomes available.
  useEffect(() => {
    if (room.localCameraStream) void devices.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.localCameraStream]);

  const shell = (content: React.ReactNode) => (
    // h-screen (definite height) not min-h-screen: the video area uses h-full/
    // flex, which only resolves against a definite height — otherwise a screen
    // share grows the page unbounded ("za duże").
    <div className="flex h-screen flex-col overflow-hidden bg-[var(--color-bg-body)]">
      <header className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
        <Video size={18} className="text-[var(--color-primary)]" />
        <span className="text-sm font-bold tracking-wide text-[var(--color-text-primary)]">
          ELGW5 - spotkanie
        </span>
      </header>
      {content}
    </div>
  );

  if (previewError) {
    return shell(
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="max-w-md text-center text-sm text-[var(--color-text-secondary)]">
          {previewError}
        </p>
      </div>,
    );
  }

  if (!entered || room.status === "idle") {
    return shell(
      <div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
        <div className="w-full max-w-md space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
          <div>
            <h1 className="text-lg font-bold text-[var(--color-text-primary)]">
              {preview?.title ?? "…"}
            </h1>
            {preview && (
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                {`${new Date(preview.starts_at).toLocaleDateString("pl-PL", { weekday: "long" })}, ${formatDateTime(preview.starts_at)}`}
                {preview.organizer_name
                  ? ` · organizator: ${preview.organizer_name}`
                  : ""}
              </p>
            )}
          </div>
          {rtcUnsupported && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              Ta przeglądarka nie obsługuje rozmów wideo (np. okno podglądu w
              aplikacji pocztowej). Skopiuj link i otwórz go w Chrome, Edge,
              Firefoksie lub Safari.
            </div>
          )}
          <label className="block text-sm">
            <span className="mb-1 block text-[var(--color-text-secondary)]">
              Twoje imię i nazwisko
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. Jan Kowalski"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-primary)]"
            />
          </label>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
            >
              Urządzenia
            </button>
            <button
              type="button"
              disabled={!preview?.can_join || !name.trim() || rtcUnsupported}
              onClick={() => void enter()}
              className="rounded-md bg-[var(--color-primary)] px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {preview?.can_join
                ? "Dołącz do spotkania"
                : preview?.status === "ended" || preview?.status === "cancelled"
                  ? "Spotkanie zakończone"
                  : "Spotkanie jeszcze się nie zaczęło"}
            </button>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Ten link daje dostęp wyłącznie do tego spotkania.
          </p>
        </div>
        <DeviceSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          devices={devices}
          onRequestAccess={devices.requestPermission}
        />
      </div>,
    );
  }

  if (room.status === "ended") {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Połączenie ze spotkaniem zostało zakończone.
        </p>
        {/* Also the landing spot after the browser froze this tab (Back button
            / app switch): reloading is the way back into the room. */}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm text-white"
        >
          Dołącz ponownie
        </button>
      </div>,
    );
  }

  if (room.status === "error") {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <p className="text-sm text-red-500">{room.error}</p>
        <button
          type="button"
          onClick={() => void room.join()}
          className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm text-white"
        >
          Spróbuj ponownie
        </button>
      </div>,
    );
  }

  return shell(
    <div className="flex min-h-0 flex-1 flex-col">
      {room.error && (
        <div className="flex items-center justify-between gap-3 border-b border-red-300 bg-red-50 px-4 py-1.5 text-xs text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <span>{room.error}</span>
          <button
            type="button"
            onClick={room.dismissError}
            className="shrink-0 font-medium hover:underline"
          >
            OK
          </button>
        </div>
      )}
      {notesVisible && (
        <div className="flex justify-end border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5">
          <button
            type="button"
            onClick={() => setNotesOpen((o) => !o)}
            className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
              notesOpen
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)]"
            }`}
          >
            <NotebookPen size={13} /> Notatki
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        <div className="min-h-0 flex-1">
          <VideoTileGrid
            me={room.me}
            localCameraStream={room.localCameraStream}
            localScreenStream={room.localScreenStream}
            localMediaState={room.mediaState}
            peers={room.peers}
            speakerId={devices.prefs.speakerId}
            onDiagnostic={room.logEvent}
          />
        </div>
        {notesVisible && notesOpen && token && (
          <aside className="w-80 shrink-0 overflow-y-auto border-l border-[var(--color-border)] bg-[var(--color-bg-surface)]">
            <div className="flex items-center justify-end px-2 pt-2">
              <button type="button" onClick={() => setNotesOpen(false)}>
                <X size={14} className="text-[var(--color-text-muted)]" />
              </button>
            </div>
            <GuestNotesPanel token={token} />
          </aside>
        )}
      </div>
      <MeetingControls
        mediaState={room.mediaState}
        onToggleMic={room.toggleMic}
        onToggleCamera={() => void room.toggleCamera()}
        onToggleScreen={() =>
          room.mediaState.screen
            ? room.stopScreenShare()
            : void room.startScreenShare()
        }
        onOpenSettings={() => setSettingsOpen(true)}
        onLeave={() => room.leave()}
      />
      <DeviceSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        devices={devices}
        onSwitchMic={(id) => void room.switchMic(id)}
        onSwitchCamera={(id) => void room.switchCamera(id)}
        onRequestAccess={async () => {
          const ok = await room.requestDevices();
          await devices.refresh();
          return ok;
        }}
        hasMedia={!!room.localCameraStream}
      />
    </div>,
  );
}
